/**
 * LAS PESTAÑAS DE ARRIBA. PURO.
 *
 * Regla del dueño para TODO Conversia (9-oct-2026): «cualquier cosa que se despliega debe aparecer
 * como un onglet». La pieza nació en la copia de Richard (rama tanda-6) y aquí se trae tal cual,
 * con los tipos de esta tienda. Si se mejora en una copia, se lleva a las demás.
 *
 * Lo que se abre para verlo o trabajarlo (un producto, una venta, un cliente, un lote, un asunto) no sale como una capa
 * encima de la pantalla: queda como una pestaña arriba, tipo navegador. La primera pestaña es
 * siempre la pantalla donde se está y no vive en esta lista: «activa = null» es estar en ella.
 *
 * Aquí va la lógica (abrir sin duplicar, el tope, cuál queda activa al cerrar, qué se guarda);
 * `store/pestanas.store.ts` solo la conecta con el navegador.
 */

/** Los de `TipoDeEntidad` (lo que abre un chip de las tablas) más el asunto de Athena. */
export type TipoDePestana = 'producto' | 'venta' | 'factura' | 'cliente' | 'paca' | 'gasto' | 'asunto';

export interface Pestana {
  /** `tipo + ':' + id`. */
  clave: string;
  tipo: TipoDePestana;
  id: string;
  titulo: string;
  /** Lo que el contenido necesita para pintarse ya. No se guarda al recargar. */
  datos?: unknown;
}

export interface EstadoDePestanas {
  /** De la más vieja a la más nueva: así se ven de izquierda a derecha. */
  abiertas: Pestana[];
  /** La clave de la que se está viendo; `null` es la pantalla. */
  activa: string | null;
}

export const SIN_PESTANAS: EstadoDePestanas = { abiertas: [], activa: null };
export const TOPE_DE_PESTANAS = 12;
const TIPOS: readonly string[] = ['producto', 'venta', 'factura', 'cliente', 'paca', 'gasto', 'asunto'];

/** Los ids de la página que enlazan cada pestaña con lo que controla (`aria-controls`). */
export const PANEL_DE_PESTANAS = 'panel-de-pestanas';
export const PANTALLA_DE_FONDO = 'pantalla-de-fondo';
/** El id del botón de una pestaña; `null` es la de la pantalla. */
export const idDePestana = (clave: string | null): string => (clave === null ? 'pestana-pantalla' : `pestana-${clave}`);

export const claveDe = (tipo: TipoDePestana, id: string): string => `${tipo}:${id}`;

/** El título que cabe en una pestaña (y en el nombre de su botón de cerrar). */
export function tituloCorto(texto: string, tope = 40): string {
  const limpio = texto.replace(/\s+/g, ' ').trim();
  return limpio.length <= tope ? limpio : `${limpio.slice(0, tope - 1).trimEnd()}…`;
}

/**
 * Abre una pestaña y la deja activa. Si ya estaba, solo la activa (conserva su título: quien
 * la abre de nuevo puede no saberlo todavía; si trae `datos` nuevos, esos sí se cambian).
 * Pasado el tope, se va la más vieja que no sea esa.
 */
export function abrir(e: EstadoDePestanas, nueva: Omit<Pestana, 'clave'>): EstadoDePestanas {
  const clave = claveDe(nueva.tipo, nueva.id);
  if (e.abiertas.some((p) => p.clave === clave)) {
    const abiertas = nueva.datos === undefined ? e.abiertas : e.abiertas.map((p) => (p.clave === clave ? { ...p, datos: nueva.datos } : p));
    return { abiertas, activa: clave };
  }
  const abiertas = [...e.abiertas, { ...nueva, clave, titulo: tituloCorto(nueva.titulo) }];
  while (abiertas.length > TOPE_DE_PESTANAS) {
    abiertas.splice(abiertas.findIndex((p) => p.clave !== clave), 1);
  }
  return { abiertas, activa: clave };
}

/** Cierra una. Si era la activa, queda la que tenía a su izquierda (o la pantalla). */
export function cerrar(e: EstadoDePestanas, clave: string): EstadoDePestanas {
  const i = e.abiertas.findIndex((p) => p.clave === clave);
  if (i < 0) return e;
  const abiertas = e.abiertas.filter((p) => p.clave !== clave);
  return { abiertas, activa: e.activa === clave ? (e.abiertas[i - 1]?.clave ?? null) : e.activa };
}

/** Va a una pestaña abierta, o a la pantalla (`null`). Una clave que no existe no mueve nada. */
export function activar(e: EstadoDePestanas, clave: string | null): EstadoDePestanas {
  if (clave === e.activa || (clave !== null && !e.abiertas.some((p) => p.clave === clave))) return e;
  return { ...e, activa: clave };
}

/** El asunto nace como «Asunto…» y toma su nombre cuando carga. */
export function renombrar(e: EstadoDePestanas, clave: string, titulo: string): EstadoDePestanas {
  const corto = tituloCorto(titulo);
  if (!corto || !e.abiertas.some((p) => p.clave === clave && p.titulo !== corto)) return e;
  return { ...e, abiertas: e.abiertas.map((p) => (p.clave === clave ? { ...p, titulo: corto } : p)) };
}

/**
 * La pestaña a la que llevan las flechas: `null` es la de la pantalla, que va primero.
 * Da la vuelta en los extremos, como el patrón de pestañas de siempre.
 */
export function vecina(e: EstadoDePestanas, hacia: -1 | 1): string | null {
  const orden: Array<string | null> = [null, ...e.abiertas.map((p) => p.clave)];
  return orden[(orden.indexOf(e.activa) + hacia + orden.length) % orden.length] ?? null;
}

/** El nombre de la pantalla donde se está, como lo dice el menú; el detalle hereda el de su lista. */
export function nombreDeLaPantalla(rutas: ReadonlyArray<{ ruta: string; etiqueta: string }> | undefined, camino: string): string {
  // La raíz solo vale para sí misma: si no, sería el nombre de cualquier ruta que el menú no conoce.
  const candidatas = (rutas ?? []).filter((r) => camino === r.ruta || (r.ruta !== '/' && camino.startsWith(`${r.ruta}/`)));
  candidatas.sort((a, b) => b.ruta.length - a.ruta.length);
  return candidatas[0]?.etiqueta ?? 'Inicio';
}

// ─── Lo que se guarda (sessionStorage) ──────────────────────────────────────

/** Una llave por usuario y negocio: lo de una cuenta no lo lee otra en el mismo navegador. */
export function llaveDeGuardado(usuarioId: string | null | undefined, negocioId: string | null | undefined): string | null {
  return usuarioId && negocioId ? `conversia.pestanas.${usuarioId}.${negocioId}` : null;
}

/** Se guarda lo mínimo para volver a pintarlas: `datos` no (el contenido lo vuelve a pedir por su id). */
export function paraGuardar(e: EstadoDePestanas): string {
  return JSON.stringify({ abiertas: e.abiertas.map(({ tipo, id, titulo }) => ({ tipo, id, titulo })), activa: e.activa });
}

/** Lo guardado, o nada si el texto no es lo que se esperaba. Nunca revienta. */
export function restaurar(texto: string | null | undefined): EstadoDePestanas {
  if (!texto) return SIN_PESTANAS;
  try {
    const crudo = JSON.parse(texto) as { abiertas?: unknown; activa?: unknown } | null;
    if (!crudo || !Array.isArray(crudo.abiertas)) return SIN_PESTANAS;
    const abiertas: Pestana[] = [];
    for (const p of crudo.abiertas as Array<Partial<Pestana> | null>) {
      if (!p || typeof p.id !== 'string' || !p.id || typeof p.titulo !== 'string' || !TIPOS.includes(p.tipo as string)) continue;
      const clave = claveDe(p.tipo as TipoDePestana, p.id);
      if (abiertas.some((x) => x.clave === clave)) continue;
      abiertas.push({ clave, tipo: p.tipo as TipoDePestana, id: p.id, titulo: tituloCorto(p.titulo) || 'Sin nombre' });
    }
    const recortadas = abiertas.slice(-TOPE_DE_PESTANAS);
    const activa = typeof crudo.activa === 'string' && recortadas.some((p) => p.clave === crudo.activa) ? crudo.activa : null;
    return { abiertas: recortadas, activa };
  } catch {
    return SIN_PESTANAS;
  }
}

/** Lee las pestañas de esa llave. Sin llave (sin sesión o sin negocio) no hay nada que leer. */
export function leer(almacen: Pick<Storage, 'getItem'> | null | undefined, llave: string | null): EstadoDePestanas {
  if (!almacen || !llave) return SIN_PESTANAS;
  try {
    return restaurar(almacen.getItem(llave));
  } catch {
    return SIN_PESTANAS;
  }
}
